import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getUTM } from "../lib/utm";
import { trackLead } from "../lib/tracking";
import { Loader2, Send } from "lucide-react";
import TurnstileField from "./TurnstileField";
import { notifyLeadSubmitted } from "./ExitIntent";
import SchoolSelector from "./SchoolSelector";
import TeacherProfileFields from "./TeacherProfileFields";
import { buildTrackingPayload } from "../lib/clickIds";
import { useSettings } from "../lib/settings";
import { leadFormCopy, isPlausiblePhone } from "../lib/leadForm";

const initial = {
  nume: "", prenume: "", email: "", telefon: "",
  specializare: "", unitate: "",
  city: "", county: "",
  uat_name: "",
  school_code: null,
  city_original: "",
  manual_school_entry: false,
  teaching_levels: [],
  teaching_level_other: "",
  specialization: "",
  specialization_other: "",
  consent_privacy: false,
  consent_marketing: false,
};

export default function LeadForm() {
  // Contact form or brochure form, chosen in Setări curs. The server applies
  // the same setting on submit, so this only decides what the visitor sees.
  const { lead_form_mode: leadFormMode } = useSettings();
  const copy = leadFormCopy(leadFormMode);
  const [form, setForm] = useState(initial);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [countyInvalid, setCountyInvalid] = useState(false);
  const turnstileRef = useRef(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_privacy) {
      toast.error("Te rugăm să accepți Politica de confidențialitate.");
      return;
    }
    if (copy.marketingRequired && !form.consent_marketing) {
      toast.error("Pentru a-ți trimite broșura și actualizări despre curs, avem nevoie de acordul tău de comunicare.");
      return;
    }
    if (copy.phoneRequired && !isPlausiblePhone(form.telefon)) {
      toast.error("Te rugăm să completezi un număr de telefon valid, ca să te putem contacta.");
      return;
    }
    if (copy.profileFields && !form.county) {
      setCountyInvalid(true);
      toast.error("Te rugăm să selectezi județul.");
      return;
    }
    if (!turnstileToken) {
      toast.error("Te rugăm să aștepți verificarea anti-spam.");
      return;
    }
    setLoading(true);
    try {
      const utm = getUTM();
      // Var B: lead capture is marketing-only. NO group selection here.
      // Default source is `homepage_form`; popup attribution flips it to `popup`.
      let extra = { source: "homepage_form" };
      try {
        const raw = sessionStorage.getItem("popupAttribution");
        if (raw) {
          const att = JSON.parse(raw);
          extra = {
            source: att.source || "popup",
            popup_trigger_type: att.popup_trigger_type || null,
            popup_cta_clicked: !!att.popup_cta_clicked,
          };
        }
      } catch { /* ignore */ }
      const referrer = (typeof document !== "undefined" && document.referrer) || null;
      const landingPage = (typeof window !== "undefined" && window.location.pathname) || null;
      // PR-T1/T3: server-side analytics sidecar (event_id, click IDs, Meta + GA4 cookies).
      const tracking = buildTrackingPayload();
      const res = await api.post("/leads", {
        ...form, ...extra, utm,
        ...tracking,
        turnstile_token: turnstileToken,
        referrer, landing_page: landingPage,
      });
      try { sessionStorage.removeItem("popupAttribution"); } catch { /* ignore */ }
      // Browser pixels — eventID matches backend CAPI for dedup.
      trackLead({ ...res.data, event_id: tracking.event_id }, 0, "RON", tracking.event_id);
      notifyLeadSubmitted();
      setDone(true);
      setForm(initial);
      setCountyInvalid(false);
      toast.success(copy.toastSuccess);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "A apărut o eroare. Încearcă din nou.");
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    // M913B — Selective PII masking. Only individual inputs that carry
    // PII (name, email, phone, free-text "other" fields, school name) get
    // `clarity-mask`. Labels, dropdowns, checkboxes, CTAs, validation
    // messages stay VISIBLE in Clarity recordings so UX analysis is useful.
    <section id="lead-form" className="scroll-mt-28 py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="bg-[#EDEEF0] rounded-3xl border border-[#E5E7EB] p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#965EA7]/10 blur-3xl" />
          <div className="relative">
            <div className="text-center">
              <span className="inline-flex items-center rounded-full bg-white border border-[#E5E7EB] px-3 py-1 text-xs font-semibold text-[#965EA7] uppercase tracking-widest">
                {copy.eyebrow}
              </span>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold text-[#373A45]">
                {copy.title}
              </h2>
              <p className="mt-3 text-[#6B7280]">{copy.subtitle}</p>
            </div>

            {done ? (
              <div className="mt-8 bg-white rounded-2xl border border-[#E5E7EB] p-8 text-center">
                <div className="font-display text-2xl text-[#373A45] font-semibold">{copy.doneTitle}</div>
                <p className="mt-2 text-[#6B7280]">{copy.doneBody}</p>
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  className="mt-5 text-[#965EA7] font-semibold hover:underline"
                  data-testid="lead-form-reset"
                >
                  Trimite altă solicitare
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lead-capture-form">
                <Field label="Prenume" required>
                  <input id="lead-prenume" name="prenume" data-testid="lead-input-prenume" required value={form.prenume} onChange={update("prenume")} className={`${cls} clarity-mask`} data-clarity-mask="true" autoComplete="given-name" autoCapitalize="words" />
                </Field>
                <Field label="Nume" required>
                  <input id="lead-nume" name="nume" data-testid="lead-input-nume" required value={form.nume} onChange={update("nume")} className={`${cls} clarity-mask`} data-clarity-mask="true" autoComplete="family-name" autoCapitalize="words" />
                </Field>
                <Field label="Email" required>
                  <input id="lead-email" name="email" data-testid="lead-input-email" required type="email" value={form.email} onChange={update("email")} className={`${cls} clarity-mask`} data-clarity-mask="true" autoComplete="email" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
                </Field>
                <Field label="Telefon" required={copy.phoneRequired} optional={!copy.phoneRequired}>
                  <input id="lead-telefon" name="telefon" data-testid="lead-input-telefon" type="tel" required={copy.phoneRequired} value={form.telefon} onChange={update("telefon")} className={`${cls} clarity-mask`} data-clarity-mask="true" autoComplete="tel" inputMode="tel" />
                </Field>
                {/* The teaching profile and the school belong to the brochure
                    form only. The contact form asks for four fields — the
                    rest is a conversation, not a questionnaire. */}
                {copy.profileFields && (<>
                {/* Structured teacher profile: ciclu + disciplină */}
                <div className="md:col-span-2">
                  <TeacherProfileFields
                    value={{
                      teaching_levels: form.teaching_levels,
                      teaching_level_other: form.teaching_level_other,
                      specialization: form.specialization,
                      specialization_other: form.specialization_other,
                    }}
                    onChange={(patch) => setForm((f) => {
                      const next = { ...f, ...patch };
                      if ("specialization" in patch || "specialization_other" in patch) {
                        const spec = patch.specialization ?? f.specialization;
                        const other = patch.specialization_other ?? f.specialization_other;
                        next.specializare = spec === "Altă disciplină" ? (other || "") : (spec || "");
                      }
                      return next;
                    })}
                    required
                    targetGroup="inform"
                  />
                </div>

                {/* Schools directory: județ + UAT + searchable school */}
                <div className="md:col-span-2">
                  <SchoolSelector
                    value={{
                      county: form.county,
                      uat_name: form.uat_name,
                      school_name: form.unitate,
                      school_code: form.school_code,
                      city_original: form.city_original,
                      manual_school_entry: form.manual_school_entry,
                    }}
                    onChange={(patch) => {
                      setForm((f) => {
                        const next = { ...f };
                        if ("county" in patch) { next.county = patch.county; setCountyInvalid(false); }
                        if ("uat_name" in patch) { next.uat_name = patch.uat_name; next.city = patch.uat_name || ""; }
                        if ("school_name" in patch) next.unitate = patch.school_name;
                        if ("school_code" in patch) next.school_code = patch.school_code;
                        if ("city_original" in patch) next.city_original = patch.city_original;
                        if ("manual_school_entry" in patch) next.manual_school_entry = patch.manual_school_entry;
                        return next;
                      });
                    }}
                    required
                    schoolRequired={false}
                  />
                </div>
                </>)}

                {/* Honeypot — invisible to humans, bots fill it */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                  name="website"
                  value=""
                  onChange={() => {}}
                />

                <div className="md:col-span-2">
                  <TurnstileField
                    ref={turnstileRef}
                    onVerify={setTurnstileToken}
                    onExpire={() => setTurnstileToken(null)}
                    onError={() => setTurnstileToken(null)}
                  />
                </div>

                <div className="md:col-span-2 mt-2 space-y-3">
                  <label className="flex items-start gap-3 text-sm text-[#373A45]" data-testid="lead-consent-privacy-label">
                    <input
                      type="checkbox"
                      checked={form.consent_privacy}
                      onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })}
                      className="mt-1 h-4 w-4 accent-[#965EA7]"
                      required
                      data-testid="lead-consent-privacy"
                    />
                    <span>
                      Am citit și sunt de acord cu{" "}
                      <Link to="/politica-confidentialitate" target="_blank" className="underline text-[#965EA7] hover:text-[#7A4986]">
                        Politica de confidențialitate
                      </Link>{" "}
                      și înțeleg cum sunt prelucrate datele mele. <span className="text-[#965EA7]">*</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-[#373A45]" data-testid="lead-consent-marketing-label">
                    <input
                      type="checkbox"
                      checked={form.consent_marketing}
                      onChange={(e) => setForm({ ...form, consent_marketing: e.target.checked })}
                      className="mt-1 h-4 w-4 accent-[#965EA7]"
                      data-testid="lead-consent-marketing"
                    />
                    <span>
                      {copy.marketingLabel}
                      {copy.marketingRequired && <span className="text-[#965EA7]"> *</span>}
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="md:col-span-2 mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold text-base px-8 py-4 shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="lead-submit-button"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                  Trimite solicitarea
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const cls =
  "w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none transition-all placeholder:text-[#373A45]/40";

function Field({ label, required, optional, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">
        {label}{required && <span className="text-[#965EA7]"> *</span>}
        {optional && <span className="ml-1.5 text-[10px] font-normal normal-case tracking-normal text-[#9A98A6]">opțional</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
